import type { HeicConvertibleAsset } from './convertHeicAsset';

import { revokeBlobUri } from '@/util/revokeBlobUri';
import { Sentry } from '@/util/sentry';

const HEIC_MIME_TYPE = /^image\/hei[cf](-sequence)?$/i;

const HEIC_EXTENSION = /\.(heic|heif|hif)$/i;

const JPEG_QUALITY = 0.9;

/** Types a browser reports when it does not know the file, so the bytes have to decide. */
const UNKNOWN_MIME_TYPES: ReadonlySet<string> = new Set(['', 'application/octet-stream']);

/** The brands an ISO base media file names itself with when it holds HEIC or HEIF images. */
const HEIC_BRANDS: ReadonlySet<string> = new Set([
  'heic',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'mif1',
  'msf1',
]);

const SNIFF_BYTES = 12;

function readHead(file: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));

    reader.onerror = () => reject(reader.error);

    reader.readAsArrayBuffer(file.slice(0, SNIFF_BYTES));
  });
}

async function hasHeicBrand(file: Blob): Promise<boolean> {
  try {
    const head = await readHead(file);

    if (head.length < SNIFF_BYTES) {
      return false;
    }

    const box = String.fromCharCode(...head.slice(4, SNIFF_BYTES));

    return box.startsWith('ftyp') && HEIC_BRANDS.has(box.slice(4));
  } catch {
    return false;
  }
}

/**
 * Browsers other than Safari cannot decode HEIC, and Chrome often reports an empty type for the
 * file, so the extension counts as well as the type, and the first bytes decide when neither
 * says anything.
 */
export async function needsHeicConversion(asset: HeicConvertibleAsset): Promise<boolean> {
  const source = asset.file;

  if (!source) {
    return false;
  }

  const name = asset.name || source.name;

  const mimeType = (asset.mimeType || source.type || '').toLowerCase();

  if (HEIC_MIME_TYPE.test(mimeType) || HEIC_EXTENSION.test(name)) {
    return true;
  }

  return UNKNOWN_MIME_TYPES.has(mimeType) && (await hasHeicBrand(source));
}

/**
 * Re-encodes a picked HEIC file as JPEG so Android and Windows recipients can view it. Drawing
 * through a canvas applies the photo's orientation and drops its metadata, GPS included. The
 * decoder is a large WebAssembly bundle that runs on the main thread, so it loads only when a HEIC
 * file is actually picked.
 *
 * A file the decoder cannot read is sent as the HEIC it is rather than refused, so the reader
 * keeps the attachment and the recipient can still download it.
 */
export async function convertHeicAsset<Asset extends HeicConvertibleAsset>(
  asset: Asset
): Promise<Asset> {
  const source = asset.file;

  if (!source || !(await needsHeicConversion(asset))) {
    return asset;
  }

  try {
    const { default: heic2any } = await import('heic2any');

    const converted = await heic2any({
      blob: source,
      toType: 'image/jpeg',
      quality: JPEG_QUALITY,
    });

    const blob = Array.isArray(converted) ? converted[0] : converted;

    const name = `${(asset.name || source.name).replace(HEIC_EXTENSION, '') || 'photo'}.jpg`;

    const file = new File([blob], name, { type: 'image/jpeg' });

    revokeBlobUri(asset.uri);

    return {
      ...asset,
      name,
      mimeType: 'image/jpeg',
      size: file.size,
      uri: URL.createObjectURL(file),
      file,
    };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'convertHeicAsset', source: 'web' },
      extra: {
        errorMessage: error instanceof Error ? error.message : undefined,
        fileSize: source.size,
      },
    });

    return { ...asset, mimeType: 'image/heic' };
  }
}
